// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Resources } from '@shared/localizations/Resources';
import { HtmlScroll } from '@components/webdesktop/elements/Scroll/HtmlScroll';
import TerceraClosePositionBodyTemplate from '@front/templates/TerceraClosePositionBodyTemplate.html';
import ClosePositionFooterTemplate from '@front/templates/ClosePositionFooterTemplate.html';
import { Control } from '../elements/Control';
import { ScreensNames } from '../UtilsClasses/FactoryConstants';
import { OrderEditViewBase } from '../panels/OrderEditViewBase';
import { TerceraWindowBase } from './TerceraWindowBase';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { ConfirmationTypesEnum } from '@shared/utils/Trading/ConfirmationTypesEnum';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { DataCache } from '@shared/commons/DataCache';
import { TradingNumericErrorChecker } from '@shared/commons/Trading/TradingNumericErrorChecker';
import { MathUtils } from '@shared/utils/MathUtils';
import moment from 'moment';
import { MarginInfoParameters } from '@shared/commons/UtilsClasses/MarginInfo/MarginInfoParameters';
import { WDSettings } from '../settings/WDGeneralSettings';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { getConfirmationMarginInfoAsync } from '@shared/commons/UtilsClasses/MarginInfo/ConfirmationMarginInfo';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { MarginInfoUtils } from '@shared/commons/UtilsClasses/MarginInfo/MarginInfoUtils';
// constructor
export class TerceraClosePositionScreen extends TerceraWindowBase {
    constructor() {
        super();
        this.FPosition = null;
        this.onShowNextTimeChanged = (event) => {
            void this.set('checkedShowNextTime', event.currentTarget.checked);
        };
        this.initialAmountToClose = null;
        this.Name = 'TerceraClosePositionScreen';
        this.headerLocaleKey = 'screen.closePosition.title';
    }
    getType() {
        return ScreensNames.ClosePositionScreen;
    }
    oncomplete() {
        this.FPosition = null;
        super.oncomplete();
        this.on('closeClick', this.closeClick);
        this.on('cancelClick', this.cancelClick);
        this.observe('AmountToClose', this.changeAmountToClose);
        this.observe('visible', this.onVisibleChanged);
        this.observe('isVisibleOTHTrading', this.updateIsAllowOTHTrading);
        void this.set('checkedShowNextTime', WDSettingsUtils.useConfirmation(ConfirmationTypesEnum.PositionClose));
        const position = this.get('position');
        const isPositionClosingAllowed = IsAllowed.IsPositionClosingAllowed(position, true).Allowed;
        this.set('forbiddenPanel', !isPositionClosingAllowed);
        void this.fillByPositionAsync(position);
        this.FPosition = position;
        Control.Ticker.Subscribe(this.TickAsync, this);
        this.addScrollToContainer('.close-position-screen-body');
    }
    oninit() {
        super.oninit();
        void this.set('numericsErrors', {});
        this.localize();
    }
    closeClick() {
        this.closePos();
        const parentContainerControl = this.get('parentContainerControl');
        if (parentContainerControl) {
            parentContainerControl.close();
        }
    }
    cancelClick() {
        const parentContainerControl = this.get('parentContainerControl');
        if (parentContainerControl) {
            parentContainerControl.close();
        }
    }
    isTotalFeeAllowed(account) {
        return account.DataCache.isAllowedForMyUser(RulesSet.FUNCTION_DISPLAY_FEES_ON_CONFIRMATIONS);
    }
    GetTotalFeeFromMarginRequest(position_1) {
        return __awaiter(this, arguments, void 0, function* (position, amountToClose = null) {
            const parameters = new MarginInfoParameters();
            parameters.account = position.Account;
            parameters.instrument = position.Instrument;
            parameters.orderType = OrderType.Market;
            let lotsToClose;
            if (!isNullOrUndefined(amountToClose)) {
                const qty = new Quantity(amountToClose, WDSettings.isQuantityInLots);
                lotsToClose = Quantity.toLots(qty, position.Instrument);
            }
            else {
                lotsToClose = position.Amount;
            }
            parameters.amountLots = lotsToClose;
            parameters.productType = position.ProductType;
            parameters.operation = position.BuySell;
            parameters.isAllowOTHTrading = this.get('isAllowOTHTrading');
            const marginInfo = yield getConfirmationMarginInfoAsync([
                { isOnlyOffMarket: false, marginInfoParameters: parameters }
            ]);
            this.updateOffmarketEnabledWarning(marginInfo.offMarketError);
            return this.buildTotalFeeFormatted(marginInfo.totalFee, position.Account.assetBalanceDefault);
        });
    }
    buildTotalFeeFormatted(totalFee, assetBalanceDefault) {
        return MarginInfoUtils.formatCalculableNumeric(totalFee, (v) => assetBalanceDefault.formatPrice(Math.abs(v)));
    }
    fillByPositionAsync(position) {
        return __awaiter(this, void 0, void 0, function* () {
            const isBuy = position.BuySell === OperationType.Buy;
            const instrument = position.Instrument;
            const account = position.Account;
            const productType = position.ProductType;
            yield this.set({
                positionId: position.PositionId,
                InstrumentName: instrument.DisplayName(),
                isBuy,
                OperationType: Resources.getResource(isBuy ? 'general.trading.position.Buy' : 'general.trading.position.Sell'),
                OpenPrice: instrument.formatPrice(position.OpenPrice),
                CurrentPrice: instrument.formatPrice(position.CurPriceClose),
                CloseGrossPL: account.formatPrice(position.getGrossPnL(false)),
                AccountName: position.Account.FullAccString,
                OrderType: Resources.getResource('property.' + OrderUtils.getOrderTypeLocalizationKey(OrderType.Market)),
                Time: moment(position.OpenTime).format('DD.MM.YYYY HH:mm:ss'),
                insName: instrument.GetInteriorID(),
                leverage: OrderUtils.GetFormattedLeverage(position.Leverage),
                productType: InstrumentUtils.GetLocalizedProductType(instrument, productType),
                showProductType: instrument.isProductTypeVisible(),
                TotalFee: this.buildTotalFeeFormatted(this.isTotalFeeAllowed(account) ? undefined : null, account.assetBalanceDefault),
                isVisibleOTHTrading: account.isAllowOTHTrading(instrument)
            });
            const inLots = WDSettings.isQuantityInLots;
            const lotSize = instrument.getLotSize();
            const amountMultiplier = inLots ? 1 : lotSize;
            let amountPrecision = instrument.getAmountPrecision(inLots, account, productType);
            let convertedAmount = inLots ? position.Amount : position.Amount * amountMultiplier;
            let SkipValidationForMaxValue = false;
            const lotStep = instrument.getLotStep(productType, account);
            if (MathUtils.mod(convertedAmount, lotStep) !== 0) {
                // #118192: Close Positions with Qty < LotSize
                amountPrecision = MathUtils.getPrecision(convertedAmount);
                SkipValidationForMaxValue = true;
            }
            if (!inLots) {
                convertedAmount = parseFloat(convertedAmount.toFixed(amountPrecision));
            }
            yield this.set('Amount', convertedAmount);
            if (!instrument.isHideRouteMode) {
                yield this.set({ Route: position.Route, showRoute: true });
            }
            else {
                yield this.set({ showRoute: false });
            }
            const routeOffset = instrument.isHideRouteMode ? 0 : 20; // calcOffsets
            const leverageOffset = routeOffset + (this.get('leverage') ? 20 : 0); // delete after UI_Refactoring TODO
            const productTypeOffset = leverageOffset + (this.get('showProductType') ? 20 : 0);
            yield this.set({
                routeOffset,
                leverageOffset, // delete after UI_Refactoring TODO
                productTypeOffset
            }); // calcOffsets end
            let step = lotStep * amountMultiplier;
            step = parseFloat(step.toFixed(amountPrecision));
            const minLot = instrument.getMinLot(productType, account);
            if (convertedAmount < minLot) {
                step = convertedAmount; // to disable numeric for #118192: Close Positions with Qty < LotSize
            }
            yield this.set({
                amountPrec: amountPrecision,
                lotStep: step,
                minAmountToClose: step,
                maxAmountToClose: convertedAmount,
                AmountToClose: convertedAmount,
                SkipValidationForMaxValue,
                allowAmountEdit: step !== convertedAmount
            });
            this.initialAmountToClose = convertedAmount;
            this.updateQtyToCloseMoreThenMaxLotWarning(position);
            this.addAdditionalInfo(instrument, account);
            // TODO
            yield this.set({
                partialCloseAllowed: true /* updatePackage.modifyRules[TerceraModifyScreen.AllowedModifyOrderRules.allowPartialClose] */
            });
            const totalFee = yield this.GetTotalFeeFromMarginRequest(position);
            void this.set({ TotalFee: totalFee });
            this.fire('onFillByPosition');
        });
    }
    addAdditionalInfo(instrument, account) {
        const additionalInfo = account.GetCommissionInfo(instrument);
        if (!additionalInfo) {
            return;
        }
        void this.set('additionalInfo', additionalInfo);
        const el = this.find('.confirmFeeInfoContainer');
        if (el) {
            HtmlScroll.addScroll(el);
        }
    }
    closePos() {
        if (TradingNumericErrorChecker.HasErrors(this)) {
            return;
        }
        // TODO. When numeric is disabled, it throws 'Value is empty' error.
        if (this.get('allowAmountEdit') && !OrderEditViewBase.CheckNumericsErrors(this.get('numericsErrors'))) {
            return;
        }
        const dc = DataCache;
        const qty = new Quantity(this.get('AmountToClose'), WDSettings.isQuantityInLots);
        dc.FOrderExecutor.closePositionQuantity(this.get('position'), qty, this.get('isAllowOTHTrading'), this.get('placedFrom'));
    }
    localize() {
        void this.set({
            positionIdText: Resources.getResource('panel.positions.pos_number') + ':',
            symbolText: Resources.getResource('panel.positions.Symbol') + ':',
            routeText: Resources.getResource('panel.positions.route') + ':',
            sideText: Resources.getResource('panel.positions.Operation') + ':',
            leverageText: Resources.getResource('panel.positions.Leverage') + ':',
            quantityText: Resources.getResource('panel.closePosition.QunatityToClose') + ':',
            orderTypeText: Resources.getResource('panel.positions.OrderType') + ':',
            openPriceText: Resources.getResource('panel.positions.Basis') + ':',
            currentPriceText: Resources.getResource('panel.positions.cur_price') + ':',
            grossPLText: Resources.getResource('panel.closePosition.Gross_P/L_ToClose') + ':',
            accountText: Resources.getResource('panel.positions.Account') + ':',
            dateTimeText: Resources.getResource('panel.positions.date_time') + ':',
            totalFeeText: Resources.getResource('screen.closePosition.EstimatedClosingFee') + ':',
            errorText: Resources.getResource('screen.closePosition.maxLotWarning'),
            productTypeText: Resources.getResource('panel.closePosition.productTypeText') + ':',
            showNextTimeText: Resources.getResource('general.messageBox.showNextTime'),
            closeText: Resources.getResource('screen.closePosition.ok'),
            cancelText: Resources.getResource('screen.closePosition.cancel')
        });
    }
    TickAsync() {
        const pos = this.FPosition;
        let amount = this.get('AmountToClose');
        if (!WDSettings.isQuantityInLots) {
            amount = amount / pos.Instrument.LotSize;
        }
        this.updateQtyToCloseMoreThenMaxLotWarning(pos);
        void this.set({
            OpenPrice: pos.Instrument.formatPrice(pos.OpenPrice),
            CurrentPrice: pos.Instrument.formatPrice(pos.CurPriceClose),
            CloseGrossPL: pos.Account.formatPrice(pos.PnLCalculator.CalculateAccNetPLByQty(amount))
        });
    }
    onteardown() {
        WDSettingsUtils.updateConfirmation(ConfirmationTypesEnum.PositionClose, this.get('checkedShowNextTime'));
        Control.Ticker.UnSubscribe(this.TickAsync, this);
        super.onteardown();
    }
    updateOffmarketEnabledWarning(offmarketEnabled) {
        void this.set({ warningText: offmarketEnabled });
    }
    updateQtyToCloseMoreThenMaxLotWarning(position) {
        if (WDSettings.warnIfQtyToCloseMoreThanMaxLot) {
            const qtyToClose = this.get('AmountToClose');
            const newErrorMode = IsAllowed.IsMaxLotHasBeenReached(position, qtyToClose);
            void this.set({ errorMode: newErrorMode });
        }
    }
    changeAmountToClose(newValue) {
        if (!isNullOrUndefined(this.FPosition)) {
            const account = this.FPosition.Account;
            void this.set({ TotalFee: this.buildTotalFeeFormatted(this.isTotalFeeAllowed(account) ? undefined : null, account.assetBalanceDefault) });
            void this.GetTotalFeeFromMarginRequest(this.FPosition, newValue).then((result) => {
                void this.set({ TotalFee: result });
            });
        }
    }
    updateIsAllowOTHTrading() {
        const isVisible = this.get('isVisibleOTHTrading');
        const isAllowOTHTrading = this.get('isAllowOTHTrading');
        if (isVisible) {
            if (isNullOrUndefined(isAllowOTHTrading)) {
                this.set('isAllowOTHTrading', BaseSettings.isAllowOTHTrading);
            }
        }
        else {
            this.set('isAllowOTHTrading', null);
        }
    }
    onVisibleChanged(newValue) {
        if (newValue) {
            const initialAmountToClose = this.initialAmountToClose;
            if (!isNullOrUndefined(initialAmountToClose)) {
                void this.set('AmountToClose', initialAmountToClose); /* #128739 */
            }
        }
    }
}
TerceraWindowBase.extendWith(TerceraClosePositionScreen, {
    data: function () {
        return {
            resizable: false,
            dockablePanel: false,
            style_addition_body: 'close-position-screen-body',
            position: null,
            InstrumentName: 'N/A',
            Route: 'N/A',
            OperationType: 'N/A',
            productType: 'N/A',
            Amount: 0.01,
            OpenPrice: 'N/A',
            CurrentPrice: 'N/A',
            CloseGrossPL: 'N/A',
            AccountName: 'N/A',
            OrderType: 'Market',
            Time: 'N/A',
            TotalFee: null,
            positionId: '',
            insName: null,
            AmountToClose: null,
            lotStep: 0.01,
            amountPrec: 0.01,
            maxAmountToClose: 0.1,
            minAmountToClose: 0.1,
            partialCloseAllowed: false,
            numericsErrors: {},
            showRoute: true,
            showProductType: false,
            showHeader: false,
            showBorder: false,
            isFIFOPosition: false,
            placedFrom: null,
            errorTop: 7,
            errorMode: false,
            errorText: '',
            header: 'Close position',
            positionIdText: 'Position ID:',
            symbolText: 'Symbol:',
            routeText: 'Route:',
            sideText: 'Side:',
            leverageText: 'Leverage:',
            productTypeText: 'Product type:',
            quantityText: 'Quantity to close:',
            orderTypeText: 'Order type:',
            openPriceText: 'Open price:',
            currentPriceText: 'Current price:',
            grossPLText: 'Gross P/L to close:',
            accountText: 'Account:',
            additionalInfo: '',
            dateTimeText: 'Date/Time:',
            totalFeeText: 'Estimated closing fee:',
            warningText: '', // offmarketEnabled warning
            isRelativeStyle: true,
            isAllowPadding: false,
            attached: true,
            overnightExecutionTextKey: 'panel.newOrderEntry.OvernightExecution',
            overnightExecutionTooltipKeyActive: 'panel.newOrderEntry.OvernightExecution.Info.NoSLTP.Active',
            overnightExecutionTooltipKeyInActive: 'panel.newOrderEntry.OvernightExecution.Info.NoSLTP.InActive',
            isVisibleOTHTrading: false,
            isAllowOTHTrading: null,
            isAllowOTHTradingEvents: {
                onActiveChanged: (isActive) => {
                    this.set('isAllowOTHTrading', isActive);
                }
            }
        };
    },
    computed: {
        formattedCurrentPrice: {
            get: function () {
                const ins = DataCache.getInstrumentByName(this.get('insName'));
                const curPrice = this.get('CurrentPrice');
                return ins ? ins.formatPrice(curPrice) : curPrice;
            },
            set: function () { }
        },
        sideStyle: function () {
            return this.get('isBuy') ? 'buy-color' : 'sell-color';
        },
        grossPLStyle: function () {
            const grossPLStr = this.get('CloseGrossPL');
            const colorClass = !grossPLStr || grossPLStr.indexOf('-') === 0 ? 'down-color' : 'up-color';
            return colorClass;
        }
    },
    partials: {
        bodyPartial: TerceraClosePositionBodyTemplate,
        footerPartial: ClosePositionFooterTemplate
    }
});
